import { LoadingOverlay } from '@mantine/core';
import { useForm } from '@mantine/form';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { TaskForm } from 'src/Components/Forms/Task/TaskForm';
import { SlateNode } from 'src/Components/Shared/SlateRenderer/SlateRenderer.types';
import {
  EMPTY_DESCRIPTION,
  isDescriptionEmpty,
  TaskFormValues,
} from 'src/Hooks/Forms/useCreateTaskForm';
import useDeleteTask from 'src/Hooks/Mutations/useDeleteTask';
import useUpdateTask from 'src/Hooks/Mutations/useUpdateTask';
import useFetchTasks from 'src/Hooks/Queries/useFetchTasks';
import { CreateTaskDto, RelatedEntityType, TaskStatus } from 'src/types/task.types';

import { Props } from './TaskEditionFormWrapper.types';

const parseDescription = (description: string | null | undefined): SlateNode[] => {
  if (!description) return EMPTY_DESCRIPTION;
  try {
    const parsed = JSON.parse(description);
    if (Array.isArray(parsed)) return parsed;
    return EMPTY_DESCRIPTION;
  } catch {
    // Legacy plain text - wrap in paragraph
    return [{ type: 'paragraph', children: [{ text: description }] }];
  }
};

const TaskEditionFormWrapper = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const { assetId, taskId } = useParams<{ assetId: string; taskId: string }>();
  const { data: tasks, isLoading: isLoadingTasks } = useFetchTasks(assetId!);
  const { mutate: updateTask, isLoading: isUpdating } = useUpdateTask(assetId!);
  const { mutate: deleteTask, isLoading: isDeleting } = useDeleteTask(assetId!);

  const task = tasks?.find(t => t.id === taskId);

  const form = useForm<TaskFormValues, (values: TaskFormValues) => CreateTaskDto>({
    initialValues: {
      title: '',
      description: EMPTY_DESCRIPTION,
      status: TaskStatus.TODO,
      category: null,
      dueDate: null,
      subCategory: null,
      assignedToId: null,
      relatedEntities: [],
      requiresOnSite: false,
    },
    validate: {
      title: (value: string) =>
        value.length < 2 ? t('common.errors.tooShort', 'Trop court') : null,
    },
    transformValues: (values: TaskFormValues) => {
      return {
        title: values.title,
        description: isDescriptionEmpty(values.description)
          ? undefined
          : JSON.stringify(values.description),
        status: values.status,
        category: values.category ?? undefined,
        subCategory: values.subCategory ?? undefined,
        dueDate: values.dueDate?.toISOString() ?? undefined,
        assignedToId: values.assignedToId ?? undefined,
        relatedEntities: values.relatedEntities.length > 0 ? values.relatedEntities : undefined,
        requiresOnSite: values.requiresOnSite,
      } as CreateTaskDto;
    },
  });

  useEffect(() => {
    if (task) {
      const relatedEntities = (task.relatedEntities ?? []).map(entity => ({
        type: entity.type as RelatedEntityType,
        id: entity.id,
      }));

      form.initialize({
        title: task.title,
        description: parseDescription(task.description),
        status: task.status,
        category: task.category ?? null,
        subCategory: task.subCategory ?? null,
        dueDate: task.dueDate ? dayjs(task.dueDate).toDate() : null,
        assignedToId: task.assignedTo?.id ?? null,
        relatedEntities,
        requiresOnSite: task.requiresOnSite ?? false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id]);

  const handleSubmit = (values: CreateTaskDto) => {
    if (!taskId) return;

    updateTask(
      { taskId, data: values },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  const handleDelete = () => {
    if (!taskId) return;
    if (
      window.confirm(t('tasks.confirmDelete', 'Êtes-vous sûr de vouloir supprimer cette tâche ?'))
    ) {
      deleteTask(taskId, {
        onSuccess: () => onClose(),
      });
    }
  };

  if (isLoadingTasks) return <LoadingOverlay visible />;

  return (
    <TaskForm
      form={form}
      onSubmit={form.onSubmit(handleSubmit)}
      onCancel={onClose}
      onDelete={handleDelete}
      isLoading={isUpdating}
      isDeleting={isDeleting}
      isEditing
    />
  );
};

export default TaskEditionFormWrapper;
